import { Button } from "@tento-ui/components/button/button";
import { css, type Style } from "@tento-ui/component-styles";

/**
 * The sheet element's stylesheet (F11): its host, layout, legend, overlay
 * and navigation rules. Moved out of sheet-element.ts unchanged; a function
 * so the module holds no state (the element's static `$styles` calls it once).
 */
export function sheetElementStyles(): Style.Compat[] {
  return [
    Button.$style,
    css`
      :host {
        --list-header-background-color: var(--bg-elevated);
        --sheet-divider-color: var(--divider);
        --std-scroll-content-background-color: var(
          --sheet-background-color,
          var(--bg)
        );
      }

      #new-row-button {
        width: 100%;
        min-width: 150px;
        opacity: 0;
        pointer-events: none;
      }
      #legend {
        position: relative;
        padding-top: var(--top-content-padding, 12px);
        min-height: 100%;
        // height: calc(100vh - var(--legend-bottom-offset, 60px) - var(--top-content-padding, 12px));
      }
      #sheet-legend {
        min-width: 150px;
        --divider-inset-top: -15px;
        --divider-inset-bottom: -15px;
        padding-bottom: 8px;
      }

      #legend,
      #sheet-header-legend,
      #sheet-footer-legend {
        opacity: 0;
        pointer-events: none;
      }
      :host([legend-ready]) #legend,
      :host([legend-ready]) #sheet-header-legend,
      :host([legend-ready]) #sheet-footer-legend {
        opacity: 1;
        pointer-events: auto;
      }

      /* Collapsed rail — KILL BY DEFAULT, CONDENSE BY SELF-DECLARATION.
         The rows are still bound and still measured — only their paint and
         hit-testing are switched off, so expanding is a width write with
         nothing to rebuild. The kill list is the legends' light-DOM CHILDREN,
         which is what keeps the chrome alive: the rail's right divider is
         #spacer::after and sheet-legend's own #root::after, neither of which
         is a child of a legend, so the strip still reads like a closed drawer
         rather than a raw gap.

         A rail that keeps NOTHING is removal with extra steps — the operator
         read the all-dead 28px strip as "the legend is missing". So a row
         frame MAY opt into a condensed projection by carrying a static
         condensed-capable attribute, and then it is exempt from the kill.
         Three parties, none inspecting another:

         - the RAIL publishes: the legend-collapsed attribute, the width, and
           the --legend-row-* tokens below. It never looks at a row.
         - a ROW frame self-declares condensed-capable — set by
           SheetLegend.Nested from its own slotted legend's declaration at
           BIND time, never by this rule and never per collapse state.
         - everything else dies exactly the way it did before, which is why generic
           sheets' 28px drawer is byte-identical.

         The exemption only exists under legend-condensed, which the rail
         flips beside legend-collapsed iff the embed set an explicit
         legendCollapsedWidthPx: condensing is an EMBED CHOICE keyed off the
         same signal that sizes the lane. Capability says a row CAN condense;
         the gate says this drawer WANTS it. Without the gate every
         DefaultRow name would survive into the default 28px strip, a
         mid-glyph fragment in a lane never sized to hold one.

         The tokens are the whole bridge. Custom properties inherit through
         shadow boundaries, so a capable row subscribes with one line per part
         inside its OWN shadow CSS — opacity: var(--legend-row-icon-opacity,
         1) — with no JS and no second bind path, and the un-collapsed state
         is byte-identical because every subscription's fallback IS the
         expanded value.

         HIT-INERTNESS IS A PAIRED TOKEN, not a derivation of the opacity one:
         pointer-events cannot be computed from an opacity, and a part at
         opacity 0 that still hit-tests is a trap (the DefaultRow icon opens a
         popover on click). Every --legend-row-X-opacity therefore ships a
         --legend-row-X-pointer beside it, consumed as
         pointer-events: var(--legend-row-X-pointer, auto). A visibility token
         was the alternative and is rejected here: the timescale badge already
         drives its own visibility from a :host(:hover) rule, which would win
         over an inherited-token declaration and pop the badge back while
         collapsed. */
      :host([legend-collapsed]) #sheet-legend > :not([condensed-capable]),
      :host([legend-collapsed])
        #sheet-header-legend
        > :not([condensed-capable]),
      :host([legend-collapsed])
        #sheet-footer-legend
        > :not([condensed-capable]),
      :host([legend-collapsed]:not([legend-condensed])) #sheet-legend > *,
      :host([legend-collapsed]:not([legend-condensed]))
        #sheet-header-legend
        > *,
      :host([legend-collapsed]:not([legend-condensed]))
        #sheet-footer-legend
        > * {
        opacity: 0;
        pointer-events: none;
      }
      :host([legend-collapsed][legend-condensed]) #sheet-legend,
      :host([legend-collapsed][legend-condensed]) #sheet-header-legend,
      :host([legend-collapsed][legend-condensed]) #sheet-footer-legend {
        --legend-row-icon-opacity: 0;
        --legend-row-icon-pointer: none;
        --legend-row-header-opacity: 0;
        --legend-row-header-pointer: none;
        --legend-row-badge-opacity: 0;
        --legend-row-badge-pointer: none;
        /* Decorative depth chrome, no pointer pair — see Nested's spine rule. */
        --legend-row-spine-opacity: 0;
        /* Let a capable FRAME shrink to the lane. Rows are min-width:
           max-content so they overflow the collapsed box instead of
           reflowing (see the clip rule below) — which is right for
           left-anchored ink, and wrong for the one mark a chart row keeps:
           the y-axis is anchored to its frame's RIGHT edge, so a frame still
           sized to the widest label parks the axis outside the lane and the
           clip eats the tick labels. Measured before this line: the axis of
           the "memory" row ended at x=92.7 in a 64px lane, i.e. every tick
           label gone. Only frames that declared themselves read this, and
           only while collapsed; everything else keeps the floor. The row's
           own content still overflows — see the label rule below, which is
           what keeps the name from reflowing inside the shrunken frame. */
        --legend-row-frame-min-width: 0;
        --legend-row-label-min-width: max-content;
        /* The name slides into the icon's place. Fading the icon alone is not
           enough — it leaves its 24px box in flow. Geometry, stated once here
           because the rail is the only party that knows a row is condensed:
           inside the rail's 8px left pad DefaultRow lays out a 24px icon, a
           1px label margin and 8px of inline padding, so the label ink starts
           at x=41 — ~23px of a 64px lane, which does not spell "network".
           This value REPLACES that 1px margin (see DefaultRow's
           #label-container rule, which owns the fallback): -32px puts the
           label container's edge at the rail's outer edge, so the ink lands
           on the 8px pad and the name gets the lane's full ~56px. */
        --legend-row-label-shift: -32px;
        /* The surviving axis keeps its geometry but not its voice: full-rail
           tick ink is sized to sit beside a label and a toggle, and alone in
           the lane it reads like the loudest thing in the card. Two text
           steps down, one ink tier down — DataLegend passes both through to
           the tick marks, falling back to the expanded values. */
        --legend-row-axis-font: var(--text-7px);
        --legend-row-axis-color: var(--text-ter);
      }
      /* REQUIRED: SheetLegend.Nested rows are min-width: max-content, so a
         28px box does not reflow them — they overflow it and paint across the
         grid. clip rather than hidden on purpose: hidden would make these
         scroll containers, and the legend rows are position: sticky against
         the sheet's scrollport. */
      :host([legend-collapsed]) #legend,
      :host([legend-collapsed]) #sheet-header-legend,
      :host([legend-collapsed]) #sheet-footer-legend {
        overflow: clip;
      }

      #sheet-header-legend {
        --sheet-legend-bg-color: var(--bg-elevated);
        padding-bottom: 1px;
        max-height: calc(100vh - 120px);
      }

      #sheet-footer-legend {
        --divider-inset-top: -15px;
        --divider-inset-bottom: -2px;
      }

      #spacer {
        position: relative;
      }

      #spacer::after {
        position: absolute;
        content: "";
        right: -0.5px;
        width: 1px;
        height: 100%;
        background: var(--divider);
      }

      /* Screen reader only - visually hidden but accessible */
      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      #root {
        /* Column geometry comes through bindGeometry. These application shell
           values have no readers inside the grid, but inheriting each resize
           forces styles to be revisited in every calendar cell. The navigation
           overlay is a sibling and still receives the shell dimensions. */
        --sidebar-width: initial;
        --sidebar-stickied-width: initial;
        --sidebar-floating-width: initial;
      }

      #navigation-loading-overlay {
        position: absolute;
        inset: 0;
        pointer-events: none;
        display: none;
        align-items: center;
        justify-content: center;
        gap: 12px;
        transform: translateX(
          calc(-1 * (var(--sidebar-width, 0px) + var(--extra-sticky-margin, 0px)) * var(--stickied, 0) / 2)
        );
        transition: transform 0.3s ease;
      }

      #navigation-loading-overlay[shown] {
        display: flex;
      }
    `,
  ];
}
